import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormikProvider, useFormik } from 'formik';
import { Prompt } from 'react-router';
import { Box, Flex } from 'reflexbox';
import { useToasts } from 'react-toast-notifications';
import styled from 'styled-components';
import { rgba } from 'polished';

import * as graphql from 'generated/graphql';
import PlainButton from 'packages/kakapo-ui-kit/components/buttons/PlainButton';
import {
	PrimaryButtonLink,
	SubmitButton,
} from 'packages/kakapo-ui-kit/components/buttons/PrimaryButton';
import { ButtonSize } from 'packages/kakapo-ui-kit/types/Button';
import Column from 'packages/kakapo-ui-kit/components/grid/Column';
import { PageTitle } from 'packages/kakapo-ui-kit/components/common/Headings';
import { ParagraphSmall } from 'packages/kakapo-ui-kit/components/common/Paragraph';
import Spinner from 'packages/kakapo-ui-kit/components/common/Spinner';
import QuillText from 'packages/kakapo-ui-kit/components/editors/QuillText';
import GraphQLErrorMessage from 'packages/kakapo-ui-kit/components/forms/errors/GraphQLErrorMessage';
import useErrorBoundary from 'packages/kakapo-ui-application/hooks/useErrrorBoundary';
import useViewer from 'packages/kakapo-ui-application/hooks/useViewer';

import FeedbackResponseAnswer from './FeedbackResponseAnswer';
import FeedbackThankYou from './FeedbackThankYou';
import useUploadAsset from 'packages/kakapo-ui-application/hooks/useUploadAsset';
import { Callout } from '@treams/treams-ui';
import { MicrophoneIcon } from '@treams/treams-icons';

const AlreadySent = styled(Box)`
	border-radius: 5px;
	background: ${(props) => rgba(props.theme.colors.employee, 0.25)};
	font-size: 16px;
`;

export default function FeedbackResponseComposer(props: {
	response: graphql.FeedbackResponse;
	secret?: string;
}) {
	const { t } = useTranslation();
	const [submitted, setSubmitted] = useState(false);
	const [error, attempt] = useErrorBoundary();

	const viewer = useViewer();

	const errors = new Set(
		(error?.extensions?.error as any)?.data?.details?.map(
			({ path }: { path: number[] }) => path[1],
		),
	);

	const { addToast } = useToasts();

	const formik = useFormik({
		initialValues: {
			answers: props.response.questions.map(
				({ id }) =>
					props.response.answers?.find(({ questionID }) => questionID === id) ?? {
						questionID: id,
						text: undefined,
						stars: undefined,
						competencyLevelIndex: undefined,
						voiceMemo: undefined,
					},
			),
		},
		onSubmit: () => {},
	});

	const [updateInternal, { loading: loadingInternal }] =
		graphql.useUpdateInternalFeedbackMutation();

	const [updateExternal, { loading: loadingExternal }] =
		graphql.useUpdateExternalFeedbackMutation();

	const loading = loadingInternal || loadingExternal;

	const [createAsset] = useUploadAsset();

	const onSave = attempt(async (submit: boolean) => {
		const answers: graphql.FeedbackResponseAnswer[] = [];

		for (const answer of formik.values.answers) {
			const voiceMemo = answer.voiceMemo as
				| graphql.VoiceMemo
				| { blob: Blob; samples: number[]; sampleRate: number };

			if (!voiceMemo || 'asset' in voiceMemo) {
				answers.push(answer);
				continue;
			}

			const asset = await createAsset(voiceMemo.blob);

			answers.push({
				...answer,
				voiceMemo: {
					asset,
					samples: voiceMemo.samples,
					sampleRate: voiceMemo.sampleRate,
				},
			});
		}

		formik.setFieldValue('answers', answers);

		const input: graphql.UpdateInternalFeedbackInput | graphql.UpdateExternalFeedbackInput = {
			responseID: props.response.id,
			answers: answers.map(({ questionID, text, stars, competencyLevelIndex, voiceMemo }) => ({
				questionID,
				text: text ?? undefined,
				stars: stars ?? undefined,
				competencyLevelIndex: competencyLevelIndex ?? undefined,
				voiceMemo: voiceMemo
					? {
							assetID: voiceMemo.asset.id,
							samples: voiceMemo.samples,
							sampleRate: voiceMemo.sampleRate,
					  }
					: undefined,
			})),
			status: submit ? graphql.FeedbackResponseStatus.Shared : graphql.FeedbackResponseStatus.Draft,
		};

		if (props.secret) {
			await updateExternal({ variables: { input: { ...input, secret: props.secret } } });
		} else {
			await updateInternal({ variables: { input } });
		}

		if (submit) {
			addToast(t('feedback.response.submit.success'), {
				appearance: 'success',
				autoDismiss: true,
			});

			setSubmitted(true);
		} else {
			addToast(t('feedback.response.draft.success'), {
				appearance: 'success',
				autoDismiss: true,
			});
		}
	});

	if (submitted) {
		return <FeedbackThankYou external={!!props.secret} />;
	}

	return (
		<FormikProvider value={formik}>
			<Prompt message={t('navigation.prompt')} when={formik.dirty} />
			{viewer && props.response.status === graphql.FeedbackResponseStatus.Shared && (
				<Box mb={4}>
					<PrimaryButtonLink size={ButtonSize.Small} to={`/user/${viewer.id}/feedback-v2/outgoing`}>
						{t('feedback.answer.backToOverview')}
					</PrimaryButtonLink>
				</Box>
			)}

			<Column mb={2}>
				<Box mb={2}>
					<PageTitle>{props.response.title || t('feedback.answer.title')}</PageTitle>
				</Box>

				{props.response.instruction && (
					<>
						<ParagraphSmall color="lightBlueDark">
							{t('feedback.answer.instructionalMessage')}
						</ParagraphSmall>
						<ParagraphSmall color="slateBlue">
							<QuillText quill={props.response.instruction} />
						</ParagraphSmall>
					</>
				)}
			</Column>

			{props.response.status === graphql.FeedbackResponseStatus.Cancelled && (
				<AlreadySent p={3} mb={4}>
					{t('feedback.answer.cancelled')}
				</AlreadySent>
			)}

			{props.response.status === graphql.FeedbackResponseStatus.Shared && (
				<AlreadySent p={3} mb={4}>
					{t('feedback.answer.alreadySent')}
				</AlreadySent>
			)}

			<Callout
				mt={3}
				mb={3}
				color="blue"
				icon={<MicrophoneIcon />}
				title={t('feedback.hint.voiceMemo.title')}
				description={t('feedback.hint.voiceMemo.description')}
			/>

			{error && <GraphQLErrorMessage error={error} />}

			{props.response.questions.map((question, index) => (
				<FeedbackResponseAnswer
					key={question.id}
					error={errors.has(index)}
					index={index}
					question={question}
					answer={
						props.response.status === graphql.FeedbackResponseStatus.Shared
							? formik.values.answers[index]
							: undefined
					}
					name={`answers[${index}]`}
					viewerCanUpdate={props.response.status === graphql.FeedbackResponseStatus.Draft}
				/>
			))}

			{props.response.status === graphql.FeedbackResponseStatus.Draft && (
				<Flex justifyContent="flex-end">
					<Column>
						{loading ? (
							<Spinner isLoading size="18px" />
						) : (
							<Flex>
								<Box mr={3}>
									<PlainButton onClick={() => onSave(false)}>
										{t('feedback.answer.saveAsDraft')}
									</PlainButton>
								</Box>
								<Box>
									<SubmitButton
										type="submit"
										value={t('feedback.answer.submit')}
										onClick={() => onSave(true)}
									/>
								</Box>
							</Flex>
						)}
					</Column>
				</Flex>
			)}
		</FormikProvider>
	);
}
